import { cn } from '../../utils/cn';

interface SkeletonProps {
  /** Shape variant for the skeleton placeholder */
  variant?: 'text' | 'circle' | 'rect';
  /** Explicit width (CSS value, e.g. '100%', '200px') */
  width?: string;
  /** Explicit height (CSS value) */
  height?: string;
  /** Additional CSS classes */
  className?: string;
}

const variantStyles: Record<NonNullable<SkeletonProps['variant']>, string> = {
  text: 'h-4 rounded-md',
  circle: 'rounded-full',
  rect: 'rounded-lg',
};

/**
 * Animated shimmer placeholder for loading states.
 *
 * Uses the `animate-shimmer` class defined in globals.css which provides
 * a gradient sweep animation. Respects `prefers-reduced-motion` via the
 * global CSS reduced-motion rules.
 *
 * Hidden from assistive technology (`aria-hidden`, `role="presentation"`).
 */
export function Skeleton({
  variant = 'text',
  width,
  height,
  className,
}: SkeletonProps) {
  return (
    <div
      className={cn(
        'animate-shimmer',
        variantStyles[variant],
        variant === 'circle' && !width && 'h-10 w-10',
        variant === 'rect' && !height && 'h-24',
        className,
      )}
      style={{ width, height }}
      aria-hidden="true"
      role="presentation"
    />
  );
}

interface SkeletonTextProps {
  /** Number of text lines to render */
  lines?: number;
  /** Additional CSS classes for the wrapper */
  className?: string;
}

/**
 * Pre-composed skeleton for text blocks with staggered line widths.
 * Provides a realistic "paragraph loading" visual.
 */
export function SkeletonText({ lines = 3, className }: SkeletonTextProps) {
  const widths = ['100%', '92%', '78%', '85%', '60%'];
  return (
    <div className={cn('flex flex-col gap-2', className)} aria-hidden="true">
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          variant="text"
          width={widths[i % widths.length]}
        />
      ))}
    </div>
  );
}

export default Skeleton;
